import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { openInGoogleMaps, formatCoordinates } from '../utils/locationTracker';
import './SupervisorJourneyMonitor.css';

function SupervisorJourneyMonitor({ user, onBack }) {
  const [journeys, setJourneys] = useState({});     // keyed by userId
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [lastUpdate, setLastUpdate] = useState(new Date());

  function getLocalDateString(date = new Date()) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  useEffect(() => {
    loadTechnicians();
  }, []);

  useEffect(() => {
    loadJourneys(selectedDate);
  }, [selectedDate]);

  const loadTechnicians = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'technician'),
        where('active', '==', true)
      );
      const snap = await getDocs(q);
      setTechnicians(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('❌ Load technicians error:', e);
    }
  };

  const loadJourneys = async (dateString) => {
    setLoading(true);
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      const start = new Date(year, month - 1, day, 0, 0, 0, 0);
      const end   = new Date(year, month - 1, day, 23, 59, 59, 999);

      const q = query(
        collection(db, 'journeyPlans'),
        where('date', '>=', start.getTime()),
        where('date', '<=', end.getTime()),
        orderBy('date', 'asc')
      );

      const snap = await getDocs(q);
      const stops = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Group by userId
      const grouped = {};
      stops.forEach(stop => {
        if (!grouped[stop.userId]) {
          grouped[stop.userId] = {
            userId: stop.userId,
            userName: stop.userName,
            stops: []
          };
        }
        grouped[stop.userId].stops.push(stop);
      });

      // Sort each tech's stops by visitOrder
      Object.values(grouped).forEach(tech => {
        tech.stops.sort((a, b) => (a.visitOrder || 999) - (b.visitOrder || 999));
      });

      setJourneys(grouped);
      setLastUpdate(new Date());
    } catch (e) {
      console.error('❌ Load journeys error:', e);
    } finally {
      setLoading(false);
    }
  };

  const getPurposeLabel = (value) => {
    const map = {
      routine_service: '🔧 Routine',
      emergency:       '🚨 Emergency',
      installation:    '⚙️ Install',
      inspection:      '🔍 Inspection',
      delivery:        '📦 Delivery',
    };
    return map[value] || value;
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const getJourneyStats = (stops) => {
    const total   = stops.length;
    const visited = stops.filter(s => s.status === 'visited').length;
    const pending = stops.filter(s => s.status === 'pending').length;
    const skipped = stops.filter(s => s.status === 'skipped').length;
    const pct     = total > 0 ? Math.round((visited / total) * 100) : 0;
    return { total, visited, pending, skipped, pct };
  };

  // Export CSV for all journeys on selected date
  const exportCSV = () => {
    let csv = `Journey Report - ${selectedDate}\nGenerated: ${new Date().toLocaleString('en-KE')}\n\n`;
    csv += `Technician,Stop #,Outlet,Address,Purpose,Status,Planned At,Visited At,Visit GPS\n`;

    Object.values(journeys).forEach(tech => {
      tech.stops.forEach(stop => {
        const gps = stop.visitLocation
          ? `${stop.visitLocation.latitude.toFixed(6)}, ${stop.visitLocation.longitude.toFixed(6)}`
          : 'N/A';
        csv += `"${tech.userName}","${stop.visitOrder || ''}","${stop.outletName}","${stop.address || ''}","${getPurposeLabel(stop.purpose)}","${stop.status}","${formatTime(stop.date)}","${formatTime(stop.visitedAt)}","${gps}"\n`;
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `journey_report_${selectedDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Which technicians to show
  const visibleJourneys = selectedTech === 'all'
    ? Object.values(journeys)
    : Object.values(journeys).filter(j => j.userId === selectedTech);

  const overallStats = (() => {
    let total = 0, visited = 0, pending = 0;
    Object.values(journeys).forEach(j => {
      total   += j.stops.length;
      visited += j.stops.filter(s => s.status === 'visited').length;
      pending += j.stops.filter(s => s.status === 'pending').length;
    });
    return { total, visited, pending, techs: Object.keys(journeys).length };
  })();

  return (
    <div className="journey-monitor">

      {/* Header */}
      <div className="jm-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="jm-header-info">
          <h1>🗺️ Journey Monitor</h1>
          <span className="jm-live">Updated {formatTimeAgo(lastUpdate)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="jm-controls">
        <div className="date-picker-wrapper">
          <label>📅 Date</label>
          <div className="date-picker-shell">
            <span className="date-picker-display">
              {selectedDate
                ? (() => {
                    const [yyyy, mm, dd] = selectedDate.split('-');
                    return `${mm} / ${dd} / ${yyyy}`;
                  })()
                : 'Select date'}
            </span>
            <input
              type="date"
              value={selectedDate}
              max={getLocalDateString()}
              onChange={e => setSelectedDate(e.target.value)}
              className="date-input-hidden"
            />
          </div>
        </div>

        <div className="date-picker-wrapper">
          <label>👤 Technician</label>
          <select
            value={selectedTech}
            onChange={e => setSelectedTech(e.target.value)}
            className="date-picker"
          >
            <option value="all">All Technicians</option>
            {technicians.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <button
          className="export-btn"
          onClick={exportCSV}
          disabled={Object.keys(journeys).length === 0}
        >
          ⬇️ Export CSV
        </button>
      </div>

      {/* Overall Stats */}
      <div className="jm-overall-stats">
        <div className="jm-stat">
          <span className="jm-stat-val" style={{ color: 'var(--sv-maroon)' }}>{overallStats.techs}</span>
          <span className="jm-stat-lbl">Technicians</span>
        </div>
        <div className="jm-stat">
          <span className="jm-stat-val" style={{ color: 'var(--sv-ink)' }}>{overallStats.total}</span>
          <span className="jm-stat-lbl">Total Stops</span>
        </div>
        <div className="jm-stat">
          <span className="jm-stat-val" style={{ color: 'var(--sv-success)' }}>{overallStats.visited}</span>
          <span className="jm-stat-lbl">Visited</span>
        </div>
        <div className="jm-stat">
          <span className="jm-stat-val" style={{ color: '#92722E' }}>{overallStats.pending}</span>
          <span className="jm-stat-lbl">Pending</span>
        </div>
      </div>

      {/* Journey Cards */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading journeys...</p>
        </div>
      ) : visibleJourneys.length === 0 ? (
        <div className="jm-empty">
          <div className="empty-icon">📭</div>
          <p>No journey plans found for {selectedDate}</p>
        </div>
      ) : (
        <div className="jm-journey-list">
          {visibleJourneys.map(tech => {
            const s = getJourneyStats(tech.stops);
            return (
              <div key={tech.userId} className="jm-tech-card">
                {/* Tech Header */}
                <div className="jm-tech-header">
                  <div className="jm-tech-avatar">
                    {tech.userName?.charAt(0).toUpperCase()}
                  </div>
                  <div className="jm-tech-info">
                    <div className="jm-tech-name">{tech.userName}</div>
                    <div className="jm-tech-summary">
                      {s.visited}/{s.total} stops · {s.pct}% complete
                    </div>
                  </div>
                  <div className="jm-progress-mini">
                    <div className="jm-progress-track">
                      <div className="jm-progress-fill" style={{ width: `${s.pct}%` }}></div>
                    </div>
                    <span className="jm-pct-label">{s.pct}%</span>
                  </div>
                </div>

                {/* Stop mini-list */}
                <div className="jm-stops-list">
                  {tech.stops.map((stop, idx) => (
                    <div key={stop.id} className={`jm-stop-row ${stop.status}`}>
                      <div className={`jm-stop-dot ${stop.status}`}></div>
                      <div className="jm-stop-content">
                        <div className="jm-stop-top">
                          <span className="jm-stop-name">{stop.outletName}</span>
                          <span className={`jm-stop-status ${stop.status}`}>
                            {stop.status === 'visited' ? '✅' : stop.status === 'skipped' ? '⏭️' : '⏳'}
                          </span>
                        </div>
                        <div className="jm-stop-meta">
                          <span className="jm-purpose">{getPurposeLabel(stop.purpose)}</span>
                          {stop.address && <span className="jm-address">· {stop.address}</span>}
                          {stop.status === 'visited' && stop.visitedAt && (
                            <span className="jm-visited-time"> · ✅ {formatTime(stop.visitedAt)}</span>
                          )}
                        </div>
                        {stop.visitLocation && (
                          <div className="jm-stop-location">
                            <span className="jm-gps">
                              📍 {formatCoordinates(stop.visitLocation.latitude, stop.visitLocation.longitude, stop.visitLocation.accuracy)}
                            </span>
                            <button
                              className="jm-map-btn"
                              onClick={() => openInGoogleMaps(
                                stop.visitLocation.latitude,
                                stop.visitLocation.longitude,
                                `${stop.outletName} - ${tech.userName}`
                              )}
                            >
                              🗺️ Map
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SupervisorJourneyMonitor;
